import { useEffect } from "react";
import { usePostPropertyHouseMutation } from "web_myplann/Api/Portfolio/Mutations/usePostPropertyHouseMutation";
import { ClientPropertyTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { BuildingCreateFormFields } from "web_myplann/Portfolio/Forms/BuildingShared/BuildingCreateFormFields";
import type { BuildingFormInput } from "web_myplann/Portfolio/Forms/BuildingShared/buildingFormSchema";
import { HOUSE_FORM_DEFAULT_VALUES } from "web_myplann/Portfolio/Forms/HouseForm/houseFormDefaults";
import { getHouseFormSchema } from "web_myplann/Portfolio/Forms/HouseForm/houseFormSchema";
import type { RealEstateType } from "web_myplann/Portfolio/Forms/RealEstateForm/realEstateTypes";
import { PortfolioFormShell } from "web_myplann/Portfolio/PortfolioShared/PortfolioFormShell";
import { useCreatePortfolioForm } from "web_myplann/Portfolio/PortfolioShared/useCreatePortfolioForm";

type CreateHouseFormProps = {
	onTypeChange: (type: RealEstateType) => void;
	registerSnapshot: (getValues: () => BuildingFormInput) => void;
	initialValues: BuildingFormInput | undefined;
};

export const CreateHouseForm = ({
	onTypeChange,
	registerSnapshot,
	initialValues,
}: CreateHouseFormProps) => {
	const { t } = useResource();

	const { mutate, isPending } = usePostPropertyHouseMutation();

	const { form, onSubmit } = useCreatePortfolioForm({
		schema: getHouseFormSchema(t),
		defaultValues: initialValues ?? HOUSE_FORM_DEFAULT_VALUES,
		mutate: ({ householdArea, ...values }, options) => mutate(values, options),
		successRoute: Routing.Portfolio.PortfolioAddRealEstateSuccess.path(),
	});

	useEffect(() => {
		registerSnapshot(form.getValues);
	}, [registerSnapshot, form.getValues]);

	return (
		<PortfolioFormShell
			onSubmit={onSubmit}
			submitLabel={t(Resources.Portfolio.AddRealEstate.submit)}
			disabled={!form.formState.isDirty}
			loading={isPending}
		>
			<BuildingCreateFormFields
				control={form.control}
				type={ClientPropertyTypeEnum.House}
				onTypeChange={onTypeChange}
			/>
		</PortfolioFormShell>
	);
};
